// react
import React, { useEffect, useMemo, useRef, useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useMatchRoute } from '@tanstack/react-router';

// material ui
import { Box, Divider } from '@mui/material';

// relative
import { useEnergyGroupNavItems } from './hooks/useEnergyGroupNavItems';
import { useMonitorReportsNavItems } from './hooks/useMonitorReportsNavItems';
import { ICON_MAP, NAV_CONFIG } from './navigationConfig';
import { NavigationPopper } from './NavigationPopper';
import type {
  NavigationContext,
  NavItem,
  NavItemConfig,
  NavSectionConfig,
  NavSection as NavSectionType,
} from './navigationTypes';
import { filterNavSections } from './navigationUtils';
import { NavSection } from './NavSection';
import SavedLinks from './SavedLinks';
import { mergeNavChildren } from './utils/mergeNavChildren';

interface NavigationTreeProps {
  drawerOpen?: boolean;
  /**
   * Navigation context for conditional rendering
   * Use this to pass user permissions, feature flags, etc.
   */
  navigationContext: NavigationContext;
  /**
   * Optional navigation configuration
   * Use this to provide a custom config or merge dynamic children
   * If not provided, uses the default NAV_CONFIG
   */
  config?: NavSectionConfig[];
}

const NavigationTree: React.FC<NavigationTreeProps> = ({
  drawerOpen = true,
  navigationContext,
  config: configProp,
}) => {
  const { t } = useTranslation('glossary');
  const matchRoute = useMatchRoute();
  const popperCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const energyGroupItems = useEnergyGroupNavItems();
  const monitorReportItems = useMonitorReportsNavItems();

  const config = React.useMemo(() => {
    const baseConfig = configProp ?? NAV_CONFIG;
    const withEnergyGroups =
      energyGroupItems.length > 0
        ? mergeNavChildren(
            baseConfig,
            'energyxplorer.deepdive',
            energyGroupItems,
          )
        : baseConfig;

    if (!monitorReportItems.length) {
      return withEnergyGroups;
    }

    return withEnergyGroups.map((section) =>
      section.id === 'monitor'
        ? {
            ...section,
            items: monitorReportItems,
          }
        : section,
    );
  }, [configProp, energyGroupItems, monitorReportItems]);

  // State for user interactions
  const [userExpandState, setUserExpandState] = useState<Map<string, boolean>>(
    new Map(),
  );
  const [anchorEl, setAnchorEl] = useState<{
    element: HTMLElement;
    items: NavItem[];
  } | null>(null);

  // Hydrate navigation config with translations and icons
  const sections = useMemo<NavSectionType[]>(() => {
    // Use provided config or default NAV_CONFIG
    const baseConfig = config ?? NAV_CONFIG;

    // First filter based on visibility conditions
    const filteredConfig = filterNavSections(baseConfig, navigationContext);

    const resolveNavigationLabel = (
      key: string,
      type: 'sections' | 'items',
    ) => {
      if (!t) return key;

      // Support explicit namespace keys like `navigation.items.xxx`.
      const directTranslation = t(key);
      if (directTranslation !== key) return directTranslation;

      // Backward compatibility for legacy unscoped keys.
      const scopedKey = `navigation.${type}.${key}`;
      const scopedTranslation = t(scopedKey);
      if (scopedTranslation !== scopedKey) return scopedTranslation;

      return key;
    };

    const hydrateItem = (item: NavItemConfig): NavItem => {
      // Support dynamic icons from hooks (stored in _icon property)
      const itemWithIcon = item as NavItemConfig & { _icon?: React.ReactNode };
      const dynamicIcon = itemWithIcon._icon;

      return {
        id: item.id,
        label: resolveNavigationLabel(item.labelKey, 'items'),
        icon:
          dynamicIcon ?? (item.iconName ? ICON_MAP[item.iconName] : undefined),
        path: item.path,
        children: item.children?.map(hydrateItem),
        disabled: item.disabled ? item.disabled(navigationContext) : false,
      };
    };

    return filteredConfig.map((section) => ({
      id: section.id,
      title: resolveNavigationLabel(section.titleKey, 'sections'),
      items: section.items.map(hydrateItem),
    }));
  }, [t, navigationContext, config]);

  const handleToggle = (itemId: string, currentExpandedState: boolean) => {
    setUserExpandState((prev) => {
      const next = new Map(prev);
      next.set(itemId, !currentExpandedState);
      return next;
    });
  };

  const clearPopperCloseTimeout = () => {
    if (popperCloseTimeoutRef.current) {
      clearTimeout(popperCloseTimeoutRef.current);
      popperCloseTimeoutRef.current = null;
    }
  };

  useEffect(
    () => () => {
      clearPopperCloseTimeout();
    },
    [],
  );

  const handlePopperOpen = (
    event: React.MouseEvent<HTMLElement>,
    children: NavItem[],
    itemId: string,
  ) => {
    clearPopperCloseTimeout();
    // Don't show popper if item is already expanded
    if (!isExpanded({ id: itemId, children } as NavItem)) {
      setAnchorEl({ element: event.currentTarget, items: children });
    }
  };

  const handlePopperClose = (delay = 0) => {
    clearPopperCloseTimeout();

    if (delay === 0) {
      setAnchorEl(null);
      return;
    }

    popperCloseTimeoutRef.current = setTimeout(() => {
      setAnchorEl(null);
      popperCloseTimeoutRef.current = null;
    }, delay);
  };

  // Check if item or any descendant is active using TanStack Router's built-in matching
  const isItemOrChildActive = (item: NavItem): boolean => {
    if (item.path && matchRoute({ to: item.path })) return true;
    if (item.children) {
      return item.children.some((child) => isItemOrChildActive(child));
    }
    return false;
  };

  // Determine if item should be expanded
  const isExpanded = (item: NavItem): boolean => {
    if (!item.children) return false;

    // If user has explicitly set a state, use that
    if (userExpandState.has(item.id)) {
      return userExpandState.get(item.id)!;
    }

    // Default: auto-expand if any child is active
    return item.children.some(isItemOrChildActive);
  };

  return (
    <Box
      sx={{
        overflowY: 'hidden',
        scrollbarGutter: 'stable',
        flex: 1,
        px: 2,
        '&:hover': {
          overflowY: 'auto',
        },
        '&::-webkit-scrollbar': {
          width: '6px',
          borderRadius: '3px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'transparent',
          borderRadius: '3px',
          transition: 'background-color 0.2s ease',
        },
        '&:hover::-webkit-scrollbar-thumb': {
          backgroundColor: (theme) => theme.palette.divider,
        },
      }}
    >
      {sections.map((section, index) => (
        <NavSection
          key={section.id}
          section={section}
          isLast={index === sections.length - 1}
          drawerOpen={drawerOpen}
          isExpanded={isExpanded}
          onToggle={handleToggle}
          onPopperOpen={handlePopperOpen}
          onPopperClose={handlePopperClose}
        />
      ))}

      <Divider sx={{ my: 1 }} />
      <SavedLinks drawerOpen={drawerOpen} onNavigate={null} />

      <NavigationPopper
        anchorEl={anchorEl?.element ?? null}
        items={anchorEl?.items ?? []}
        onClose={() => handlePopperClose()}
        onMouseEnter={clearPopperCloseTimeout}
        onMouseLeave={() => handlePopperClose(120)}
      />
    </Box>
  );
};

export default NavigationTree;
